import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import type { FilterOption } from './FilterSelect';

interface FilterAsyncSelectProps {
  label: string;
  value: FilterOption | null;
  onChange: (value: FilterOption | null) => void;
  loadOptions: (input: string) => Promise<FilterOption[]>;
  placeholder?: string;
  disabled?: boolean;
}

const DEBOUNCE_MS = 300;

/**
 * FilterSelect's server-searched sibling — options come from loadOptions()
 * as the user types, debounced, instead of a fixed list. Replaces
 * react-select's AsyncSelect so the panel renders one dropdown look
 * everywhere instead of a third-party list library alongside MUI's.
 */
const FilterAsyncSelect: React.FC<FilterAsyncSelectProps> = ({
  label,
  value,
  onChange,
  loadOptions,
  placeholder,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const id = ++requestId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      loadOptions(inputValue)
        .then(results => {
          if (requestId.current !== id) return;
          // Keep the current selection visible even once it scrolls out of
          // the latest search results.
          const merged =
            value && !results.some(r => r.value === value.value) ? [value, ...results] : results;
          setOptions(merged);
        })
        .finally(() => {
          if (requestId.current === id) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [open, inputValue, loadOptions, value]);

  return (
    <Autocomplete
      size="small"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      value={value}
      onChange={(_, opt) => onChange(opt)}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      getOptionLabel={opt => opt.label}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      filterOptions={x => x}
      loading={loading}
      disabled={disabled}
      renderInput={params => (
        // shrink: without it, the unfocused label sits over the placeholder's slot.
        <TextField
          {...params}
          label={label}
          placeholder={placeholder ?? `All ${label}`}
          slotProps={{ inputLabel: { shrink: true } }}
        />
      )}
    />
  );
};

export default FilterAsyncSelect;
